
import {
  defineComponent,
  inject,
  PropType,
  reactive,
  ref,
  Ref,
  watchEffect,
} from "vue";
import { DialogGenre, RowId } from "@/interfaces/common.interface";
import { ElMessage } from "element-plus";
import { CheckMsg, InfoMsg } from "@/constants/message.constant";
import { GyoushuModel } from "@/interfaces/kaisha.interface";
type ValidateFieldCallback = (message?: string | Error) => void;
import {
  checkGyoushuCd,
  createGyoushu,
  getGyoushuById,
  updateGyoushu,
} from "@/api/kaisha.api";

export default defineComponent({
  props: {
    title: String as PropType<string>, //ダイアログタイトル
    operation: String as PropType<DialogGenre>, //ダイアログ機能（登録/修正）
  },
  emits: ["search"],
  async setup(props, { emit }) {
    //親からの変数
    const dialogFormVisible = inject("dialogFormVisible") as Ref<boolean>;
    const rowId = inject("rowId") as Ref<RowId>;

    const form = reactive({
      id: 0,
      gyoushuCd: "",
      gyoushuNm: "",
    } as GyoushuModel);
    const formRef = ref();
    const loading = ref(false);
    //入力情報のチェックルール
    const rules = {
      gyoushuCd: [
        {
          required: true,
          validator: async (
            self: any,
            value: string,
            callback: ValidateFieldCallback
          ) => {
            if (!value) {
              callback(CheckMsg.CHK_GYOUSHU_CD);
            } else if (/\D/g.test(value)) {
              callback(CheckMsg.CHK_ISNUM);
            } else if (value.length !== 3) {
              callback(CheckMsg.CHK_GYOUSHU_CD_LEN);
            } else {
              try {
                await checkGyoushuCd({
                  id: rowId.value,
                  databaseField: "gyoushu_bunrui_cd",
                  fieldName: self.name,
                  fieldValue: value,
                });
                callback();
              } catch (error) {
                callback(error);
              }
            }
          },
          trigger: "blur",
          name: "業種分類コード",
        },
      ],
      gyoushuNm: [
        {
          required: true,
          message: CheckMsg.CHK_GYOUSHU2,
          trigger: "blur",
        },
      ],
    };

    //フォームリセット
    const clearForm = () => {
      formRef.value.resetFields();
      dialogFormVisible.value = false;
      rowId.value = undefined;
    };

    //登録/修正ボタンイベント
    const handleCreateOrUpdate = () => {
      formRef.value?.validate(async (valid: unknown) => {
        if (valid) {
          loading.value = true;
          try {
            if (props.operation === DialogGenre.Add) {
              await createGyoushu({
                gyoushuCd: form.gyoushuCd,
                gyoushuNm: form.gyoushuNm,
              });
              ElMessage({
                type: "success",
                message: InfoMsg.INFO_GYOUSHU_ADDED,
              });
            } else {
              await updateGyoushu(form.id, {
                gyoushuNm: form.gyoushuNm,
              });

              ElMessage({
                type: "success",
                message: InfoMsg.INFO_GYOUSHU_UPDATED,
              });
            }
            loading.value = false;
            clearForm();
            //再度検索
            emit("search");
          } catch (error) {
            loading.value = false;
          }
        } else {
          return false;
        }
      });
    };

    //修正モード時、選択行の情報取得
    const getGyoushuInfo = async (id: number) => {
      const {
        data: { result },
      } = await getGyoushuById(id);
      form.id = result.id;
      form.gyoushuCd = result.gyoushu_bunrui_cd;
      form.gyoushuNm = result.gyoushu_bunrui_nm;
    };

    watchEffect(async () => {
      if (props.operation === DialogGenre.Edit && rowId.value) {
        await getGyoushuInfo(rowId.value as number);
      }
    });

    return {
      dialogFormVisible,
      form,
      formRef,
      loading,
      rules,
      DialogGenre,
      handleCreateOrUpdate,
      clearForm,
    };
  },
});
